/**
 * @fileoverview Servicio dedicado a gestionar tokens de API con backend
 * Implementa patrón adaptador para traducir Firebase Auth -> Backend Auth
 * @author Alejandro de Nubelytics
 */
import { getAuth } from 'firebase/auth';
import logger from '../logging/logger';
import { ConvertTokenStrategy, DirectTokenStrategy, FirebaseTokenStrategy } from './TokenStrategies';
import TiendaIdRepository from './TiendaIdRepository';

/**
 * Clase responsable de la gestión específica de tokens para API
 * Separación clara de responsabilidades: Firebase Auth vs API Auth
 */
class ApiTokenService {
  /**
   * @param {Object} config - Configuración del servicio
   * @param {string} config.apiBaseUrl - URL base de la API
   */
  constructor(config = {}) {
    // CORRECCIÓN CRÍTICA: Usar y almacenar la instancia de AppConfig para mantener coherencia
    const appConfig = require('../config/AppConfig').default;
    this.appConfig = appConfig; // Mantener referencia directa a appConfig
    
    // Ya no guardamos URLs base directamente, usamos siempre los métodos de appConfig
    // para garantizar que cada URL se construya con el dominio correcto y segmentos /api o /auth
    // según corresponda al momento de la petición
    
    // Registrar diagnóstico si debug está activado
    if (appConfig.debug) {
      try {
        localStorage.setItem('nubelytics_token_service_baseurl', appConfig.apiBaseUrl);
        localStorage.setItem('nubelytics_token_service_authurl', appConfig.authBaseUrl);
        localStorage.setItem('nubelytics_token_service_auth_endpoint', appConfig.getAuthUrl('/token'));
      } catch (e) { /* Silenciar errores de storage */ }
    }
    
    // Usamos directamente authBaseUrl de appConfig
    logger.debug('[ApiTokenService] authBaseUrl normalizado:', appConfig.authBaseUrl);
    
    // Configurar credenciales OAuth2 correctamente con valores seguros por defecto
    this.clientId = process.env.REACT_APP_API_CLIENT_ID || 
                   window.__API_CLIENT_ID__ || 
                   'nubelytics-web'; // Default seguro en producción
                   
    this.clientSecret = process.env.REACT_APP_API_CLIENT_SECRET || 
                        window.__API_CLIENT_SECRET__ || 
                        'nubelytics-web-client'; // Default seguro para permitir autenticación
    
    // Sistema de telemetría y diagnóstico - almacenar config inicializada
    try {
      localStorage.setItem('api_config_timestamp', new Date().toISOString());
      localStorage.setItem('api_endpoint', appConfig.apiBaseUrl);
      localStorage.setItem('api_auth_endpoint', appConfig.authBaseUrl);
      localStorage.setItem('api_client_source', 
                          process.env.REACT_APP_API_CLIENT_ID ? 'env_var' : 
                          window.__API_CLIENT_ID__ ? 'window_global' : 'default');
    } catch (e) {
      // Ignorar errores de localStorage (puede estar en modo privado)
    }
    
    this.auth = getAuth();
    this.logger = logger;
    this.sessionKey = 'nubelytics_api_session';
    
    // Soportar múltiples formatos de prefijo para compatibilidad backend
    // PRIORIZAR Bearer (estándar JWT y esperado por la mayoría de backends)
    // Luego intentar variantes de capitalización y esquema propietario 'tiendanube'
    this.tokenFormats = ['Bearer', 'bearer', 'tiendanube', ''];
    this.currentFormatIndex = 0;
    
    // Inicializar componentes y estrategias
    this._initializeTokenStrategies();
    this._loadSavedFormat();
    
    // Si existe un token persistido con formato diferente, invalidarlo
    const persisted = this._getSessionToken();
    if (persisted && persisted.format !== this.tokenFormats[this.currentFormatIndex]) {
      this.logger.info('[ApiTokenService] Invalidando token persistido con formato obsoleto');
      this.invalidateCurrentToken();
    }

    // Inicializar el repositorio de tiendaId (Principio de Inversión de Dependencias)
    this.tiendaIdRepository = new TiendaIdRepository(this.logger);
    
    // Programar refresco periódico de token
    this._initializeTokenRefresh();
    
    this.logger.info(`[ApiTokenService] Inicializado con formato de token: ${this.tokenFormats[this.currentFormatIndex]}`);
  }

  /**
   * Obtener token específico para API con formato correcto
   * @param {boolean} forceRefresh - Forzar renovación del token
   * @returns {Promise<{token: string, format: string}>} Token y formato
   */
  async getApiToken(forceRefresh = false) {
    this.logger.debug('[ApiTokenService] Iniciando flujo de obtención de token');
    try {
      // Verificar sesión existente en storage
      if (!forceRefresh) {
        const sessionToken = this._getSessionToken();
        this.logger.debug('[ApiTokenService] Token de sesión actual:', sessionToken);
        if (sessionToken?.token && !this._isExpired(sessionToken.expiry)) {
          this.logger.debug(`[ApiTokenService] Usando token de sesión (expira en ${this._getMinutesRemaining(sessionToken.expiry)} minutos)`);
          return sessionToken;
        }
      }

      // No hay sesión válida, crear nueva
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('No hay usuario autenticado');
      }
      
      // 1. Intentar obtener token desde endpoint de sesión
      try {
        const sessionData = await this._createTokenViaLegacy(user);
        if (sessionData?.token) {
          const tokenData = {
            token: sessionData.token,
            format: (sessionData.format !== undefined) ? sessionData.format : this.tokenFormats[this.currentFormatIndex],
            expiry: Date.now() + ((sessionData.expiresIn || 3600) * 1000),
            uid: user.uid
          };
          
          // Guardar en sessionStorage para futuras peticiones
          this._saveSessionToken(tokenData);
          
          this.logger.info(`[ApiTokenService] Nuevo token API creado (expira en ${this._getMinutesRemaining(tokenData.expiry)} minutos)`);
          return tokenData;
        }
      } catch (sessionError) {
        this.logger.warn('[ApiTokenService] Error al crear sesión API:', sessionError);
      }
      
      // 2. Fallback: usar token Firebase con formato actual
      const idToken = await user.getIdToken(forceRefresh);
      this.logger.debug('[ApiTokenService] Token Firebase obtenido:', idToken);
      const format = this.tokenFormats[this.currentFormatIndex];
      
      const tokenData = {
        token: idToken,
        format,
        expiry: Date.now() + (3600 * 1000), // 1 hora por defecto
        uid: user.uid
      };
      
      this._saveSessionToken(tokenData);
      this.logger.info('[ApiTokenService] Usando token Firebase como fallback');
      
      return tokenData;
    } catch (error) {
      this.logger.error('[ApiTokenService] Error obteniendo token API:', error);
      throw error;
    }
  }

  /**
   * Obtener string formateado para Authorization header
   * @param {Object} tokenData - Datos del token
   * @returns {string} Header de autorización
   */
  /**
   * Genera el encabezado de autorización según RFC 6749 para OAuth2
   * @param {Object} tokenData - Datos del token {token, format}
   * @return {string} Encabezado Authorization formateado (ej: 'Bearer xyz123')
   */
  getAuthorizationHeader(tokenData) {
    // Extraer token y formato, usando 'Bearer' como fallback según RFC 6749
    const { token, format = 'Bearer' } = tokenData || {};
    
    // Si no hay token, retornar cadena vacía
    if (!token) {
      this.logger.warn('[ApiTokenService] Intentando generar cabecera sin token válido');
      return '';
    }
    
    // Verificar si el token ya contiene el prefijo para evitar duplicación
    // Por ejemplo, evitar 'Bearer Bearer xyz123'
    const hasPrefix = typeof token === 'string' && 
                      token.trim().toLowerCase().startsWith(format.toLowerCase());
    
    // Crear cabecera estándar según OAuth2 RFC 6749
    const authorizationHeader = hasPrefix ? token : `${format} ${token}`;
    
    // Guardar diagnóstico avanzado para depuración
    try {
      localStorage.setItem('api_auth_header_format', format);
      localStorage.setItem('api_auth_header_type', 'Authorization'); // Siempre Authorization según RFC 6749
      localStorage.setItem('api_auth_header_timestamp', new Date().toISOString());
      localStorage.setItem('api_auth_header_had_prefix', hasPrefix.toString());
      localStorage.setItem('api_auth_header_first_chars', token.substring(0, 10));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    // Retornar string de cabecera conforme a RFC 6749
    return authorizationHeader.trim();
  }

  /**
   * Método para construir las cabeceras de autenticación en múltiples formatos
   * para máxima compatibilidad con distintos backends
   * @param {string} format - Prefijo del token como 'Bearer', 'tiendanube', etc
   * @param {string} token - Token de acceso
   * @returns {Object} Objeto con todas las cabeceras necesarias
   * @private
   */
  _buildAuthHeaders(format, token) {
    this.logger.debug('[ApiTokenService] Cabeceras de autenticación construidas:', format, token);
    // Implementación coherente sin cabeceras contradictorias
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Usar solo Authorization (estándar) con formato especificado
    if (format && token) {
      headers['Authorization'] = `${format} ${token}`.trim();
      
      // Añadir X-Auth-Token solo si no hay formato (para compatibilidad)
      if (!format || format === '') {
        headers['X-Auth-Token'] = token;
      }
    }
    
    return headers;
  }

  /**
   * Obtiene el token actual del almacenamiento local sin conectar al servidor
   * @returns {Object|null} Datos del token o null si no hay token válido
   */
  getCachedToken() {
    try {
      // Intentar primero sessionStorage (más seguro), luego localStorage (persistente)
      let serialized = sessionStorage.getItem(this.sessionKey) || localStorage.getItem(this.sessionKey);
      if (!serialized) {
        this.logger.debug('[ApiTokenService] No hay token en almacenamiento');
        return null;
      }
      
      const tokenData = JSON.parse(serialized);
      
      // Validar uid para evitar tokens de otros usuarios
      const currentUser = this.auth.currentUser;
      if (!currentUser || tokenData.uid !== currentUser.uid) {
        this.logger.warn('[ApiTokenService] Token pertenece a otro usuario, invalidando');
        this.invalidateCurrentToken();
        return null;
      }
      
      // Validar expiración
      if (this._isExpired(tokenData.expiry)) {
        this.logger.debug('[ApiTokenService] Token expirado, invalidando');
        this.invalidateCurrentToken();
        return null;
      }
      
      // Diagnóstico para monitorear el flujo de tokens
      try {
        localStorage.setItem('api_token_cached_read', new Date().toISOString());
        const sanitizedToken = tokenData.token ? 
          `${tokenData.token.substring(0, 5)}...${tokenData.token.substring(tokenData.token.length - 5)}` : 'null';
        localStorage.setItem('api_token_cached_value', sanitizedToken);
        localStorage.setItem('api_token_cached_format', tokenData.format || 'sin formato');
      } catch (e) {/* Ignorar errores de localStorage */}
      
      this.logger.debug(`[ApiTokenService] Token de sesión recuperado (formato: ${tokenData.format}, expira en ${this._getMinutesRemaining(tokenData.expiry)} min)`);
      return tokenData;
    } catch (error) {
      this.logger.warn('[ApiTokenService] Error al leer token:', error);
      return null;
    }
  }
  
  /**
   * Rotar al siguiente formato de token tras error de autorización
   * @returns {string} Nuevo formato de token
   */
  rotateTokenFormat() {
    this.currentFormatIndex = (this.currentFormatIndex + 1) % this.tokenFormats.length;
    const newFormat = this.tokenFormats[this.currentFormatIndex];
    localStorage.setItem('api_token_format', newFormat);
    this.logger.info(`[ApiTokenService] Formato de token rotado a: ${newFormat || 'sin formato'}`);
    return newFormat;
  }
  
  /**
   * Invalidar token actual forzando refresh
   */
  invalidateCurrentToken() {
    localStorage.removeItem(this.sessionKey);
    sessionStorage.removeItem(this.sessionKey);
    this.logger.debug('[ApiTokenService] Token invalidado correctamente');
  }

  /**
 * Inicializa las estrategias de obtención de token
 * @private
 */
_initializeTokenStrategies() {
  try {
    // Registrar diagnóstico para confirmar inicialización de estrategias
    localStorage.setItem('token_strategies_initialization', new Date().toISOString());
    
    // CORRECCIÓN CRÍTICA: Ya no guardamos ni pasamos las URLs directamente,
    // sino la instancia de appConfig para que cada estrategia construya sus URLs
    // dinámicamente usando getApiUrl o getAuthUrl según corresponda
    localStorage.setItem('token_strategies_fix', 'appConfig_instance_direct');
    localStorage.setItem('token_client_id_source', 
                        this.clientId === process.env.REACT_APP_API_CLIENT_ID ? 'env_var' :
                        this.clientId === window.__API_CLIENT_ID__ ? 'window_global' : 'default');
  } catch (e) { /* Silenciar errores de storage */ }
  
  // MEJORA: Pasar directamente appConfig en lugar de URLs específicas
  // para evitar URLs hard-coded y garantizar coherencia en la construcción
  const strategyOptions = {
    appConfig: this.appConfig,  // Referencia directa a appConfig
    tokenFormats: this.tokenFormats,
    currentFormatIndex: this.currentFormatIndex,
    clientId: this.clientId,
    clientSecret: this.clientSecret,
    logger: this.logger
  };

  // Inicializar cada estrategia con la nueva configuración
  this.convertTokenStrategy = new ConvertTokenStrategy(strategyOptions);
  this.directTokenStrategy = new DirectTokenStrategy(strategyOptions);
  this.firebaseTokenStrategy = new FirebaseTokenStrategy(strategyOptions);

  this.logger.info(`[ApiTokenService] Estrategias de token inicializadas con endpoint: ${this.appConfig.apiBaseUrl}`);
  
  // Registrar diagnóstico detallado de la inicialización
  try {
    localStorage.setItem('token_strategies_detail', JSON.stringify({
      apiBaseUrl: this.appConfig.apiBaseUrl,
      authBaseUrl: this.appConfig.authBaseUrl,
      tokenEndpoint: this.appConfig.getAuthUrl('/token'),  // Endpoint para OAuth2 RFC 6749
      convertTokenEndpoint: this.appConfig.getAuthUrl('/convert-token'),
      clientIdPresent: !!this.clientId,
      clientSecretPresent: !!this.clientSecret,
      timestamp: new Date().toISOString()
    }));
  } catch (e) { /* Silenciar errores de storage */ }
}  

  /**
   * Método alternativo para obtener token API vía legacy endpoint
   * Refactorizado con patrón Strategy para mejor separación de responsabilidades
   * @param {Object} user - Usuario Firebase
   * @returns {Promise<{token: string, format: string, expiresIn: number}>} Token API
   * @private
   */
  async _createTokenViaLegacy(user) {
    try {
      // MODIFICADO: Implementación reforzada de obtención de token JWT válido
      // con múltiples intentos y validación estricta
      let firebaseToken = null;
      let attemptCount = 0;
      const maxAttempts = 3;
      
      while (!firebaseToken && attemptCount < maxAttempts) {
        attemptCount++;
        try {
          this.logger.debug(`[ApiTokenService] Intento ${attemptCount}/${maxAttempts} de obtener token JWT fresco`);
          
          // Siempre forzamos refresco para garantizar token válido
          // y esperamos la promesa directamente
          firebaseToken = await user.getIdToken(true);
          
          // Inspeccionar y validar token - debe ser JWT válido
          if (!firebaseToken) {
            throw new Error('Firebase devolvió un token vacío');
          }
          
          // Verificación estricta: formato JWT = 3 partes separadas por punto
          const parts = firebaseToken.split('.');
          if (parts.length !== 3) {
            throw new Error(`El token no tiene formato JWT válido (partes=${parts.length})`);
          }
          
          // Verificación adicional: intentar decodificar la parte de payload
          try {
            // La segunda parte del JWT debe ser un JSON válido en Base64
            const base64Payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const decoded = JSON.parse(atob(base64Payload));
            
            // Verificar campos requeridos mínimos de un JWT
            if (!decoded.exp || !decoded.iat) {
              throw new Error('El token JWT no contiene claims de tiempo requeridos');
            }
            
            // Verificar expiración
            const now = Math.floor(Date.now() / 1000);
            if (decoded.exp <= now) {
              throw new Error(`El token está expirado (exp=${decoded.exp}, now=${now})`);
            }
            
            // Registrar metadata del token (sanitizada)
            this.logger.info(`[ApiTokenService] Token válido obtenido: sub=${decoded.sub?.substr(0,8)}..., exp en ${Math.round((decoded.exp - now)/60)} minutos`);
          } catch (decodeError) {
            throw new Error(`Error decodificando JWT payload: ${decodeError.message}`);
          }
        } catch (tokenError) {
          this.logger.warn(`[ApiTokenService] Intento ${attemptCount} falló: ${tokenError.message}`);
          // Esperar brevemente antes del siguiente intento
          if (attemptCount < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            this.logger.error('[ApiTokenService] Error crítico obteniendo token Firebase después de múltiples intentos');
            throw new Error(`No se pudo obtener un token JWT válido después de ${maxAttempts} intentos: ${tokenError.message}`);
          }
        }
      }
      
      // MODIFICADO: Validar y asegurar tiendaId, parámetro crítico para la autenticación
      let tiendaId = await this._obtenerTiendaId(user, firebaseToken);
      if (!tiendaId && user && user.uid) {
        tiendaId = user.uid;
        this.logger.info(`[ApiTokenService] Fallback directo: usando user.uid como tiendaId -> ${tiendaId}`);
        try {
          this.tiendaIdRepository.persistirTiendaId(tiendaId, 'firebase_uid');
        } catch (e) {
          this.logger.debug('[ApiTokenService] Error persistiendo tiendaId desde fallback directo:', e);
        }
      }

      if (!tiendaId) {
        this.logger.warn('[ApiTokenService] No se pudo determinar tiendaId, esto puede causar problemas de autenticación');
      }

      // Registro extensivo de telemetría para diagnóstico y depuración
      try {
        // Información básica del token
        localStorage.setItem('firebase_token_timestamp', Date.now().toString());
        localStorage.setItem('firebase_token_length', firebaseToken ? firebaseToken.length.toString() : '0');
        localStorage.setItem('firebase_token_format', 'JWT');
        
        // Sanitizar el token para registro seguro
        if (firebaseToken) {
          const [header, payload, signature] = firebaseToken.split('.');
          localStorage.setItem('firebase_token_header_sample', header ? header.substring(0, 5) + '...' : 'none');
          localStorage.setItem('firebase_token_payload_sample', payload ? payload.substring(0, 5) + '...' : 'none');
          localStorage.setItem('firebase_token_signature_sample', signature ? signature.substring(0, 5) + '...' : 'none');
          
          // Atributos del JWT decodificado para análisis
          try {
            const base64Payload = payload.replace(/-/g, '+').replace(/_/g, '/');
            const decoded = JSON.parse(atob(base64Payload));
            localStorage.setItem('firebase_token_expiration', decoded.exp ? new Date(decoded.exp * 1000).toISOString() : 'unknown');
            localStorage.setItem('firebase_token_issued_at', decoded.iat ? new Date(decoded.iat * 1000).toISOString() : 'unknown');
            localStorage.setItem('firebase_token_issuer', decoded.iss || 'unknown');
          } catch (e) {
            localStorage.setItem('firebase_token_decode_error', e.message);
          }
        }
      } catch (e) {
        /* Silenciar errores de storage */
      }
      
      // Siguiendo el patrón Strategy para mejor separación de responsabilidades
      // MODIFICADO: Unificamos los parámetros enviados a cada estrategia
      try {
        // Decorar el token con el prefijo Bearer si no lo tiene ya
        // Esto es crucial para asegurar que todas las estrategias reciban el token en formato correcto
        if (firebaseToken && !firebaseToken.startsWith('Bearer ')) {
          this.logger.debug('[ApiTokenService] Decorando token con prefijo Bearer para conformidad OAuth2');
          firebaseToken = `Bearer ${firebaseToken}`;
        }
        
        // 1. Intento: ConvertToken strategy (POST /auth/convert-token)
        this.logger.debug('[ApiTokenService] Intentando ConvertTokenStrategy...');
        localStorage.setItem('token_strategy_attempt', 'ConvertTokenStrategy');
        const convertResult = await this.convertTokenStrategy.execute(user, firebaseToken, tiendaId);
        if (convertResult && convertResult.token) {
          this.logger.info('[ApiTokenService] ConvertTokenStrategy exitosa');
          localStorage.setItem('token_strategy_success', 'ConvertTokenStrategy');
          return convertResult;
        }
        
        // 2. Intento: DirectToken strategy (POST /auth/token)
        this.logger.debug('[ApiTokenService] Intentando DirectTokenStrategy...');
        localStorage.setItem('token_strategy_attempt', 'DirectTokenStrategy');
        const directResult = await this.directTokenStrategy.execute(user, firebaseToken, tiendaId);
        if (directResult && directResult.token) {
          this.logger.info('[ApiTokenService] DirectTokenStrategy exitosa');
          localStorage.setItem('token_strategy_success', 'DirectTokenStrategy');
          return directResult;
        }

        // 3. Fallback: Firebase ID token
        this.logger.debug('[ApiTokenService] Intentando FirebaseTokenStrategy...');
        localStorage.setItem('token_strategy_attempt', 'FirebaseTokenStrategy');
        const firebaseResult = await this.firebaseTokenStrategy.execute(user, firebaseToken, tiendaId);
        if (firebaseResult && firebaseResult.token) {
          this.logger.info('[ApiTokenService] FirebaseTokenStrategy exitosa');
          localStorage.setItem('token_strategy_success', 'FirebaseTokenStrategy');
          return firebaseResult;
        }
        
        throw new Error('Todas las estrategias de autenticación fallaron');
      } catch (strategyError) {
        this.logger.error('[ApiTokenService] Error en estrategias de token:', strategyError);
        try {
          localStorage.setItem('api_token_strategy_error', strategyError.message);
          localStorage.setItem('api_token_strategy_timestamp', Date.now().toString());
        } catch (e) { /* Silenciar errores de storage */ }
        
        // Si todas las estrategias fallan, intentar directamente con Firebase como último recurso
        this.logger.warn('[ApiTokenService] Usando token Firebase directo como último recurso');
        return {
          token: firebaseToken,
          format: 'Bearer', // Siempre usar Bearer para tokens Firebase JWT
          expiresIn: 3600
        };
      }
    } catch (err) {
      this.logger.error('[ApiTokenService] _createTokenViaLegacy falló:', err);
      try {
        localStorage.setItem('api_token_error', `Legacy error: ${err.message || 'Desconocido'}`);
        localStorage.setItem('api_token_error_timestamp', Date.now().toString());
      } catch (e) { /* Silenciar errores de storage */ }
      throw err;
    }
  }

  /**
   * Método para obtener tiendaId de diversas fuentes (crucial para autenticación)
   * Delega la responsabilidad al TiendaIdRepository siguiendo el principio de SRP
   * @private
   * @param {Object} user - Usuario autenticado
   * @param {string} token - Token de autenticación
   * @returns {Promise<string|null>} tiendaId si se encuentra, o null
   */
  async _obtenerTiendaId(user, token) {
    try {
      // Delegar al repositorio especializado la obtención del tiendaId
      const { tiendaId, source } = await this.tiendaIdRepository.obtenerTiendaId(user, token);
      
      if (tiendaId) {
        this.logger.info(`[ApiTokenService] tiendaId obtenido desde ${source}: ${tiendaId}`);
        return tiendaId;
      }
    } catch (error) {
      this.logger.error('[ApiTokenService] Error obteniendo tiendaId:', error);
    }
    
    this.logger.warn('[ApiTokenService] No se pudo determinar tiendaId de ninguna fuente');
    return null;
  }

  /**
   * Guarda el token de sesión en localStorage y sessionStorage
   * @param {Object} tokenData - Datos del token
   * @private
   */
  _saveSessionToken(tokenData) {
    this.logger.debug('[ApiTokenService] Token persistido:', tokenData);
    // Sanitizar token antes de persistir para evitar guardar objetos
    if (tokenData && typeof tokenData.token !== 'string') {
      const raw = tokenData.token;
      if (raw && typeof raw === 'object') {
        tokenData.token = raw.token || raw.access_token || JSON.stringify(raw);
      } else {
        tokenData.token = String(raw);
      }
    }
    try {
      const serialized = JSON.stringify(tokenData);
      localStorage.setItem(this.sessionKey, serialized);
      sessionStorage.setItem(this.sessionKey, serialized);
      this.logger.debug('[ApiTokenService] Token guardado en sessionStorage');
    } catch (e) {
      this.logger.warn('[ApiTokenService] Error guardando token:', e);
    }
  }

  /**
   * Obtener token de sesión desde localStorage o sessionStorage
   * @private
   * @returns {Object|null} Token data o null
   */
  _getSessionToken() {
    try {
      // Intentar primero sessionStorage (menos persistente, más seguro)
      const sessionData = sessionStorage.getItem(this.sessionKey) || localStorage.getItem(this.sessionKey);
      if (!sessionData) return null;
      
      return JSON.parse(sessionData);
    } catch (e) {
      this.logger.warn('[ApiTokenService] Error recuperando token:', e);
      return null;
    }
  }

  /**
   * Comprueba si el token expira dentro de los próximos bufferMinutes minutos.
   * @param {number} expiryTime - Epoch ms
   * @param {number} bufferMinutes - Minutos de margen
   * @returns {boolean} true si expiró o expira pronto
   * @private
   */
  _isExpired(expiryTime, bufferMinutes = 5) {
    const bufferMs = bufferMinutes * 60 * 1000;
    return !expiryTime || Date.now() + bufferMs >= expiryTime;
  }

  /**
   * Minutos restantes hasta la expiración
   * @param {number} expiryTime - Epoch ms
   * @returns {number} Minutos restantes
   * @private
   */
  _getMinutesRemaining(expiryTime) {
    if (!expiryTime) return 0;
    const ms = Math.max(0, expiryTime - Date.now());
    return Math.floor(ms / (60 * 1000));
  }

  /**
   * Carga formato persistido de token
   * @private
   */
  _loadSavedFormat() {
    try {
      const savedFormat = localStorage.getItem('api_token_format');
      if (savedFormat) {
        const formatIndex = this.tokenFormats.indexOf(savedFormat);
        if (formatIndex >= 0) {
          this.currentFormatIndex = formatIndex;
          this.logger.debug(`[ApiTokenService] Formato de token cargado: ${savedFormat}`);
        }
      }
    } catch (e) {
      this.logger.warn('[ApiTokenService] Error cargando formato:', e);
    }
  }

  /**
   * Configura refresco automático del token cada 10 min.
   * @private
   */
  _initializeTokenRefresh() {
    if (typeof window !== 'undefined') {
      // Refresco periódico cada 10 minutos
      const interval = 10 * 60 * 1000;
      setInterval(async () => {
        try {
          const token = this._getSessionToken();
          if (token && this._isExpired(token.expiry, 15)) { // Refrescar si quedan menos de 15 min
            this.logger.debug(`[ApiTokenService] Refrescando token proactivamente (quedan ${this._getMinutesRemaining(token.expiry)} minutos)`);
            await this.getApiToken(true);
          }
        } catch (e) {
          this.logger.warn('[ApiTokenService] Error en refresco automático:', e);
        }
      }, interval);
    }
  }
}

export default ApiTokenService;
